import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

/**
 * Full-bleed, parallax interior-page hero (Apple product-page feel).
 * Vivid image fills the viewport with a forest overlay; the background moves
 * slightly slower than the foreground as the visitor scrolls in.
 */
const PageHeader = ({ overline, title, lede, image, imageAlt, status }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <header ref={ref} className="relative min-h-[82vh] w-full overflow-hidden flex items-end">
      {image && (
        <>
          <motion.img
            src={image}
            alt={imageAlt}
            style={{ y, scale }}
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f12]/70 via-[#0d1f12]/35 to-[#0d1f12]/92" />
        </>
      )}
      <motion.div style={{ y: textY, opacity: fade }} className="relative w-full max-w-[1440px] mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-40">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease }}>
          <div className="flex items-center gap-4">
            {overline && (
              <span className="inline-flex items-center gap-3 text-xs font-semibold tracking-[0.2em] uppercase text-[#e7c46b]">
                <span className="h-px w-8 bg-[#d3a53a]" />{overline}
              </span>
            )}
            {status && (
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 rounded-full border border-white/30 text-white">{status}</span>
            )}
          </div>
          <h1 className="mt-5 font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl max-w-[18ch] text-white">
            {title}
          </h1>
          {lede && <p className="mt-6 text-base md:text-lg text-white/85 leading-relaxed max-w-2xl">{lede}</p>}
        </motion.div>
      </motion.div>
    </header>
  );
};

export default PageHeader;
